import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomerDashboard from "./CustomerDashboard";
import CustomersBreakdown from "./CustomersBreakdown";
import CustomerList from "./CustomerList";
import { Outlet, useLocation } from "react-router-dom";

const Customer = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState("list");
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Determine if we're on mobile/tablet
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // Dynamic margin calculation for better responsiveness
  const getMainContentClasses = () => {
    if (isMobile) {
      // On mobile, always use minimal margin regardless of sidebar state
      return "ml-0 px-2 sm:px-4";
    } else if (isTablet) {
      // On tablet, use smaller margins
      return isSideBarOpen ? "ml-16 md:ml-80" : "ml-12 md:ml-24";
    } else {
      // Desktop behavior (your original logic but refined)
      return isSideBarOpen ? "ml-20 lg:ml-80" : "ml-16 md:ml-24";
    }
  };

  // Dynamic padding for sections
  const getSectionPadding = () => {
    if (isMobile) {
      return "p-3 sm:p-4";
    } else if (isTablet) {
      return "p-4 md:p-5";
    } else {
      return "p-4 md:p-6 lg:p-8";
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: "list",
      label: "Customer List",
      component: <CustomerList archived={false} />,
    },
    {
      id: "breakdown",
      label: "Customers Breakdown",
      component: <CustomersBreakdown />,
    },
    {
      id: "archived",
      label: "Archived Customers",
      component: <CustomerList archived={true} />,
    },
  ];

  return (
    <>
      {location.pathname === "/admin/customers" && (
        <div className="min-h-screen bg-gray-50">
          <div className="flex flex-col w-screen">
            <div
              className={`transition-all duration-300 ease-in-out ${getMainContentClasses()}`}
            >
              {/* Dashboard section */}
              <div className={getSectionPadding()}>
                <div
                  className="w-full overflow-hidden"
                  style={
                    isMobile
                      ? {}
                      : {
                          maxWidth: isSideBarOpen
                            ? "calc(100vw - 25%)"
                            : "calc(100vw - 15vw)",
                        }
                  }
                >
                  <CustomerDashboard />
                </div>
              </div>

              {/* Tabbed section for Customers Breakdown and Customer List */}
              <div
                className={getSectionPadding()}
                style={
                  isMobile
                    ? {}
                    : {
                        maxWidth: isSideBarOpen
                          ? "calc(100vw - 25%)"
                          : "calc(100vw - 15vw)",
                      }
                }
              >
                <div className="w-full max-w-none overflow-hidden">
                  {/* Tab Navigation */}
                  <div className="mb-6">
                    <div className="border-b border-gray-200">
                      <nav className="flex space-x-8" aria-label="Tabs">
                        {tabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                              activeTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div
                    className="tab-content"
                    style={
                      isMobile
                        ? {}
                        : {
                            maxWidth: isSideBarOpen
                              ? "calc(100vw - 25%)"
                              : "calc(100vw - 15vw)",
                          }
                    }
                  >
                    {tabs.find((tab) => tab.id === activeTab)?.component}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render child route when the path includes "/admin/customers/:id" */}
      {location.pathname.startsWith("/admin/customers/") && (
        <div
          className={`transition-all duration-300 ease-in-out min-h-screen ${getMainContentClasses()}`}
        >
          <div className="w-full max-w-none overflow-hidden">
            <Outlet />
          </div>
        </div>
      )}
    </>
  );
};

export default Customer;
